import { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { generateAIInsight } from "./ai";
import Sidebar from "./Sidebar";
import DashboardView from "./DashboardView";
import Campaigns from "./Campaigns";
import Agents from "./Agents";
import Leads from "./Leads";
import Revenue from "./Revenue";

function App() {
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("");
  const [expenses, setExpenses] = useState([]);
  const [agents, setAgents] = useState([]);
  const [aiInsight, setAiInsight] = useState("");
  const [loadingAI, setLoadingAI] = useState(false);
  const [filterDate, setFilterDate] = useState("");
  const [revenueAmount, setRevenueAmount] = useState("");
  const [revenueCategory, setRevenueCategory] = useState("");
  const [revenue, setRevenue] = useState([]);
  const [agentName, setAgentName] = useState("");
  const [agentSalary, setAgentSalary] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [campaignCost, setCampaignCost] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [canUploadExpenses, setCanUploadExpenses] = useState(false);
  const [canUploadRevenue, setCanUploadRevenue] = useState(false);
  const [conversions, setConversions] = useState("");
  const [inputDate, setInputDate] = useState("");
  const [session, setSession] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const userId = session?.user?.id;
  const [showGuide, setShowGuide] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [lastSync, setLastSync] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState("last_30_days");

  const [leadCreatedDate, setLeadCreatedDate] = useState("");
  const [leadAccountOpenedDate, setLeadAccountOpenedDate] = useState("");
  const [leadKycCompletedDate, setLeadKycCompletedDate] = useState("");
  const [leadActivatedDate, setLeadActivatedDate] = useState("");

// Add Login Function - Supabase
  const handleLogin = async () => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      alert(error.message);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  const downloadSampleCSV = (type) => {
    let csvContent = "";


    if (type === "expenses") {
      csvContent = `date,amount,category,agent_name,campaign_name
2024-01-01,500,Ads,John Doe,Facebook Ads
2024-01-02,300,Operations,Jane Smith,Google Ads`;
    }

    if (type === "revenue") {
      csvContent = `date,amount,agent_name,campaign_name,conversions
2024-01-01,1500,John Doe,Facebook Ads,10
2024-01-02,1200,Jane Smith,Google Ads,8`;
    }

    if (type === "agents") {
      csvContent = `name,salary
John Doe,5000
Jane Smith,5000`;
    }

    if (type === "campaigns") {
      csvContent = `name,cost
    Facebook Ads,2000
    Google Ads,3000`;
    }

    if (type === "leads_master") {
      csvContent = `agent_name,campaign_name,created_date,account_opened_date,kyc_completed_date,activated_date
John Doe,Facebook Ads,2024-01-01,2024-01-02,2024-01-03,2024-01-04`;
    }


    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${type}_sample.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const updateSyncTime = () => {
    const now = new Date();
    setLastSync(now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
  };

  // 👉 Fetch expenses
  const fetchExpenses = async () => {
    const { data, error } = await supabase
      .from("expenses")
      .select("*")
      .eq("user_id", userId)
      .order("id", { ascending: false });

    if (!error) {
      setExpenses(data);
      updateSyncTime();
    }
  };

  // 👉 Fetch revenue
  const fetchRevenue = async () => {
    const { data, error } = await supabase
      .from("revenue")
      .select("*")
      .eq("user_id", userId)
      .order("id", { ascending: false });

    if (!error) {
      setRevenue(data);
      updateSyncTime();
    }
  };

  // 👉 Fetch agents
  const fetchAgents = async () => {
    const { data, error } = await supabase
      .from("agents")
      .select("*")
      .eq("user_id", userId)
      .order("id", { ascending: false });

    if (!error) {
      setAgents(data);
      updateSyncTime();
    }
  };

  // 👉 Fetch campaigns
  const fetchCampaigns = async () => {
    const { data, error } = await supabase
      .from("campaigns")
      .select("*")
      .eq("user_id", userId)
      .order("id", { ascending: false });

    if (!error) {
      setCampaigns(data);
      updateSyncTime();
    }
  };

  const fetchLeads = async () => {
    const { data, error } = await supabase
      .from("leads_master")
      .select("*")
      .eq("user_id", userId);
      
    if (error) {
      console.error("Error fetching leads:", error);
    } else {
      setLeads(data || []);
      updateSyncTime();
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return new Date().toISOString();

    const parts = dateStr.split("-");
    if (parts.length === 3 && parts[0].length === 4) {
      return dateStr;
    }

    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }

    return new Date(dateStr).toISOString();
  };

  // 👉 Handle file upload from new dashboard buttons
  const handleFileUpload = (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    const fileExtension = file.name.split('.').pop().toLowerCase();

    const parseData = async (data) => {
      let records = [];

      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      const authUserId = userData.user?.id;
      if (!authUserId) throw new Error("Not authenticated. Please sign in again.");
      console.log("[UPLOAD] session userId:", userId, "authUserId:", authUserId);

      const agentMap = Object.fromEntries(
        agents.map(a => [a.name.trim().toLowerCase(), a.id])
      );
      const campaignMap = Object.fromEntries(
        campaigns.map(c => [c.name.trim().toLowerCase(), c.id])
      );

      if (type === "expenses") {
        records = data.map(row => {
          const agentId = agentMap[row.agent_name?.trim().toLowerCase()];
          const campaignId = campaignMap[row.campaign_name?.trim().toLowerCase()];

          if (!agentId || !campaignId) {
            throw new Error(`❌ Mapping failed for row: ${JSON.stringify(row)}`);
          }

          return {
            amount: Number(row.amount),
            category: row.category,
            date: formatDate(row.date),
            agent_id: agentId,
            campaign_id: campaignId,
            user_id: authUserId   // ✅ ADD THIS
          };
        });
      }

      if (type === "revenue") {
        records = data.map((row) => {
          const agentId = agentMap[row.agent_name?.trim().toLowerCase()];
          const campaignId = campaignMap[row.campaign_name?.trim().toLowerCase()];

          if (!agentId || !campaignId) {
            throw new Error(`❌ Mapping failed for row: ${JSON.stringify(row)}`);
          }

          return {
            amount: Number(row.amount),
            date: formatDate(row.date),
            agent_id: agentId,
            campaign_id: campaignId,
            source: row.source ?? row.category ?? null,
            user_id: authUserId
          };
        });
      }

      if (type === "agents") {
        records = data.map((row) => ({
          name: row.name,
          salary:
            row.salary === null || row.salary === undefined || row.salary === ""
              ? null
              : Number(row.salary),
          user_id: authUserId
        }));
      }

      if (type === "campaigns") {
        records = data.map((row) => ({
          name: row.name,
          // schema uses budget; CSV may still provide cost
          budget: Number(row.budget ?? row.cost ?? 0),
          user_id: authUserId
        }));
      }

      if (type === "leads_master") {
        records = data.map((row) => {
          const agentId = agentMap[row.agent_name?.trim().toLowerCase()];
          const campaignId = campaignMap[row.campaign_name?.trim().toLowerCase()];
          return {
            agent_id: agentId || null,
            campaign_id: campaignId || null,
            created_date: row.created_date ? formatDate(row.created_date) : null,
            account_opened_date: row.account_opened_date ? formatDate(row.account_opened_date) : null,
            kyc_completed_date: row.kyc_completed_date ? formatDate(row.kyc_completed_date) : null,
            activated_date: row.activated_date ? formatDate(row.activated_date) : null,
            user_id: authUserId
          };
        });
      }

      // Use upsert for campaigns + agents so duplicate names don't fail inserts
      const { data: insertedData, error } =
        type === "campaigns" || type === "agents"
          ? await supabase
              .from(type)
              .upsert(records, { onConflict: "name,user_id" })
              .select()
          : await supabase.from(type).insert(records).select(); // 👈 REQUIRED to confirm insert

      console.log("INSERT RESULT:", insertedData, error);

      if (error) {
        alert(error.message);
      } else {
        alert(`${type} uploaded successfully`);
        console.log("Inserted rows:", insertedData);

        if (type === "expenses") fetchExpenses();
        if (type === "revenue") fetchRevenue();
        if (type === "agents") fetchAgents();
        if (type === "campaigns") fetchCampaigns();
        if (type === "leads_master") fetchLeads();
      }
//      );
    };

    if (fileExtension === 'csv') {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: function (results) {
          parseData(results.data);
        },
      });
    } else if (fileExtension === 'xlsx') {
      const reader = new FileReader();
      reader.onload = (evt) => {
        const workbook = XLSX.read(evt.target.result, { type: "binary" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(sheet);
        parseData(data);
      };
      reader.readAsBinaryString(file);
    } else {
      alert("Unsupported file type. Please upload CSV or XLSX.");
    }
  };


  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);  

  // 👉 Load data on page load
  useEffect(() => {
    if (session) {
      fetchExpenses();
      fetchRevenue();
      fetchAgents();
      fetchCampaigns();
      fetchLeads();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);


  // 👉 Update upload permissions based on agents and campaigns
  useEffect(() => {
    setCanUploadExpenses(agents.length > 0 && campaigns.length > 0);
    setCanUploadRevenue(agents.length > 0 && campaigns.length > 0);
  }, [agents, campaigns]);

  const handleForgotPassword = async () => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: "https://app.anvexaai.com/reset-password",
    });

    if (error) {
      alert(error.message);
    } else {
      alert("Password reset email sent.");
    }
  };

  // 👉 Add expense
  const addExpense = async () => {
    const selected = campaigns.find(c => String(c.id) === selectedCampaign);

    const { error } = await supabase.from("expenses").insert([
      {
        amount: Number(amount),
        category: category || (selected ? selected.name : "General"),
        campaign_id: selectedCampaign || null,
        agent_id: selectedAgent || null,
        date: inputDate || new Date().toISOString(),
        user_id: userId
      },
    ]);

    if (error) {
      alert(error.message);
    } else {
      alert("Expense added!");
      fetchExpenses();
    }
  };


  // 👉 Add revenue
  const addRevenue = async () => {
    const { error } = await supabase.from("revenue").insert([
      {
        amount: Number(revenueAmount), // ✅ FIXED
        category: revenueCategory,
        agent_id: selectedAgent || null,
        campaign_id: selectedCampaign || null,
        conversions: Number(conversions) || 0,
        date: filterDate || new Date().toISOString(),
        user_id: userId
      },
    ]);

    if (error) {
      alert(error.message);
    } else {
      alert("Revenue added!");
      fetchRevenue();
    }
  };

  // 👉 Add agent
  const addAgent = async () => {
    const { error } = await supabase.from("agents").insert([
      {
        name: agentName,
        salary: Number(agentSalary),
        user_id: userId   // ✅ ADD THIS
      },
    ]);

    if (error) {
      alert(error.message);
    } else {
      alert("Agent added!");
      setAgentName("");
      setAgentSalary("");
      fetchAgents(); // refresh list
    }
  };

  // 👉 Add lead
  const addLead = async () => {
    const { error } = await supabase.from("leads_master").insert([
      {
        agent_id: selectedAgent || null,
        campaign_id: selectedCampaign || null,
        created_date: leadCreatedDate || new Date().toISOString(),
        account_opened_date: leadAccountOpenedDate || null,
        kyc_completed_date: leadKycCompletedDate || null,
        activated_date: leadActivatedDate || null,
        user_id: userId
      },
    ]);

    if (error) {
      alert(error.message);
    } else {
      alert("Lead added!");
      fetchLeads();
    }
  };

  // 👉 Add campaign

  const addCampaign = async () => {
    const { error } = await supabase.from("campaigns").upsert(
      [
        {
          name: campaignName,
          cost: Number(campaignCost),
          user_id: userId
        },
      ],
      { onConflict: "name,user_id" }
    );

    if (error) {
      alert(error.message);
    } else {
      alert("Campaign added!");
      setCampaignName("");
      setCampaignCost("");
      fetchCampaigns(); // refresh list
    }
  };



  const resetDatabase = async () => {
    const confirmDelete = window.confirm(
      "⚠️ This will delete ALL data (expenses, revenue, agents, campaigns). Continue?"
    );

    if (!confirmDelete) return;

    const { error } = await supabase.rpc("reset_all_tables");

    if (error) {
      alert(error.message);
    } else {
      alert("Database reset successfully!");

      // ✅ 1. Clear frontend state immediately
      setExpenses([]);
      setRevenue([]);
      setAgents([]);
      setCampaigns([]);
      setLeads([]);

      // ✅ 2. Clear AI insight (THIS WAS MISSING)
      setAiInsight("");

      // ✅ 3. Optional: clear funnel derived state if you have one
      // setFunnelData(null);

      // ✅ 4. Then refetch (optional, but safe)
      fetchExpenses();
      fetchRevenue();
      fetchAgents();
      fetchCampaigns();
      fetchLeads();
    }
  };

  // 👉 GLOBAL DATE FILTER LOGIC
  function isWithinRange(dateStr, range) {
    if (!dateStr) return false;
    const now = new Date();
    const d = new Date(dateStr);
    switch (range) {
      case "last_7_days": return (now - d) <= 7 * 24 * 60 * 60 * 1000;
      case "last_30_days": return (now - d) <= 30 * 24 * 60 * 60 * 1000;
      case "last_90_days": return (now - d) <= 90 * 24 * 60 * 60 * 1000;
      case "all_time": return true;
      default: return true;
    }
  }

  const globalFilteredLeads = leads.filter(l => isWithinRange(l.created_date, dateRange));
  const globalFilteredRevenue = revenue.filter(r => isWithinRange(r.date, dateRange));
  const globalFilteredExpenses = expenses.filter(e => isWithinRange(e.date, dateRange));

  const totalExpenseNum = globalFilteredExpenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);
  const totalExpense = totalExpenseNum.toFixed(2);
  const totalRevenueNum = globalFilteredRevenue.reduce((sum, r) => sum + Number(r.amount || 0), 0);
  const profitNum = totalRevenueNum - totalExpenseNum;

  /* const generateInsight = () => {
    const categories = {};
    filteredExpenses.forEach(e => {
      categories[e.category] = (categories[e.category] || 0) + e.amount;
    });

    const total = filteredTotalExpense;

    if (Object.keys(categories).length === 0) {
      return "No expenses to analyze.";
    }

    const maxCategory = Object.keys(categories).reduce((a, b) =>
      categories[a] > categories[b] ? a : b
    );

    const maxAmount = categories[maxCategory];
    const ratio = maxAmount / total;

    if (ratio > 0.7) {
      return `⚠️ ${maxCategory} dominates (${(ratio * 100).toFixed(1)}%). High dependency risk.`;
    }

    if (Object.keys(categories).length < 3) {
      return `⚠️ Low expense diversity. Add more balanced spending.`;
    }

    // Profit analysis
    if (profit < 0) {
      return `⚠️ Business is running at a loss. Profit: ₹${profit}`;
    } else {
      let insight = `✅ Business is profitable. Profit: ₹${profit}, ROI: ${roi}%, CPA: ₹${cpa}\n\n`;

      // Agent insights
      const profitableAgents = agentPerformance.filter(a => a.profit > 0);
      const lossAgents = agentPerformance.filter(a => a.profit < 0);

      if (profitableAgents.length > 0) {
        insight += `Profitable Agents: ${profitableAgents.map(a => `${a.name} (₹${a.profit})`).join(", ")}\n`;
      }
      if (lossAgents.length > 0) {
        insight += `Loss-Making Agents: ${lossAgents.map(a => `${a.name} (₹${a.profit})`).join(", ")}\n`;
      }

      // Campaign insights
      const profitableCampaigns = campaignPerformance.filter(c => c.roi > 0);
      const lossCampaigns = campaignPerformance.filter(c => c.roi < 0);

      if (profitableCampaigns.length > 0) {
        insight += `Profitable Campaigns: ${profitableCampaigns.map(c => `${c.name} (${c.roi}%)`).join(", ")}\n`;
      }
      if (lossCampaigns.length > 0) {
        insight += `Loss-Making Campaigns: ${lossCampaigns.map(c => `${c.name} (${c.roi}%)`).join(", ")}\n`;
      }

      return insight;
    }
  }; */

  const categoryMap = {};

  expenses.forEach((e) => {
    const cat = e.category;
    categoryMap[cat] = (categoryMap[cat] || 0) + Number(e.amount);
  });

  const filteredExpenses = expenses.filter(e =>
    filterDate ? e.date.startsWith(filterDate) : true
  );

  const filteredTotalExpense = filteredExpenses.reduce(
    (sum, e) => sum + Number(e.amount),
    0
  );

  const filteredCategoryMap = {};

  filteredExpenses.forEach((e) => {
      let cat = e.category;

      if (!cat || cat.trim() === "") {
        const campaign = campaigns.find(
          (c) => Number(c.id) === Number(e.campaign_id)
        );
        cat = campaign ? campaign.name : "Other";
      }

      filteredCategoryMap[cat] =
        (filteredCategoryMap[cat] || 0) + Number(e.amount || 0);
    });

  const chartData = Object.keys(filteredCategoryMap)
    .map((key) => ({
      name: key.trim(),
      amount: Number(filteredCategoryMap[key]) || 0,
      percentage:
        filteredTotalExpense > 0
          ? ((filteredCategoryMap[key] / filteredTotalExpense) * 100).toFixed(1)
          : 0
    }))
    .filter((item) => item.amount > 0);
//  const avgExpense = filteredExpenses.length > 0 ? (filteredTotalExpense / filteredExpenses.length).toFixed(2) : 0;

//  const topCategory = Object.keys(filteredCategoryMap).length > 0 ? Object.keys(filteredCategoryMap).reduce((a, b) => filteredCategoryMap[a] > filteredCategoryMap[b] ? a : b) : "None";

//  const monthlySpend = filteredTotalExpense; // Assuming filtered is monthly

  const totalRevenue = revenue.reduce(
    (sum, r) => sum + Number(r.amount || 0),
    0).toFixed(2) 
  ;

  const profit = totalRevenue - totalExpense;

  const totalLeadsCount = leads.length;
  const accountsOpenedCount = leads.filter(l => l.account_opened_date && String(l.account_opened_date).trim() !== "").length;
  const kycCompletedCount = leads.filter(l => l.kyc_completed_date && String(l.kyc_completed_date).trim() !== "").length;
  const activatedCount = leads.filter(l => l.activated_date && String(l.activated_date).trim() !== "").length;

  const activationRate = totalLeadsCount > 0 ? ((activatedCount / totalLeadsCount) * 100).toFixed(2) : 0;
  const totalLeads = totalLeadsCount;

  const totalConversions = revenue.reduce(
    (sum, r) => sum + Number(r.conversions || 0),
    0
  ) + (activatedCount > 0 ? activatedCount : totalLeadsCount);

  const cpa = totalConversions > 0 ? totalExpense / totalConversions : 0;

  const roi = totalExpense > 0 ? (((totalRevenue - totalExpense) / totalExpense) * 100).toFixed(2) : 0;

  // Agent performance
  const agentPerformance = agents.map(agent => {
    const agentRevenue = revenue
      .filter(r => r.agent_id === agent.id)
      .reduce((sum, r) => sum + Number(r.amount || 0), 0);
    const agentCost = Number(agent.salary);
    const agentProfit = agentRevenue - agentCost;
    return { ...agent, revenue: agentRevenue, profit: agentProfit };
  });

  // Campaign performance
  const campaignPerformance = campaigns.map(campaign => {
    const campaignrevenue = globalFilteredRevenue.filter(
      r => Number(r.campaign_id) === Number(campaign.id)
    );

    const totalRevenue = Number(
      campaignrevenue.reduce((sum, r) => sum + Number(r.amount || 0), 0).toFixed(2)
    );

    const totalConversions = campaignrevenue.reduce(
      (sum, r) => sum + Number(r.conversions || 0),
      0
    );

    const cost = Number(campaign.cost || 0);
    const profit = Number((totalRevenue - cost).toFixed(2));
    const roi = cost > 0 ? ((profit / cost) * 100).toFixed(2) : 0;
    const cpa = totalConversions > 0 ? (cost / totalConversions).toFixed(2) : 0;
//    const canUploadExpenses = agents.length > 0 && campaigns.length > 0;
//    const canUploadRevenue = agents.length > 0 && campaigns.length > 0;

    return {
      ...campaign,
      revenue: totalRevenue,
      conversions: totalConversions,
      cost,
      profit,
      roi,
      cpa,
    };
  });

  const inputClass = "w-full p-3 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-600 outline-none text-slate-800 bg-white placeholder-slate-400";

  const searchFilter = (item) => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return Object.values(item).some(val => 
      val !== null && val !== undefined && String(val).toLowerCase().includes(searchLower)
    );
  };

  const searchedLeads = globalFilteredLeads.filter(searchFilter);
  const searchedAgentPerformance = agentPerformance.filter(searchFilter);
  const searchedCampaignPerformance = campaignPerformance.filter(searchFilter);
  const searchedExpenses = globalFilteredExpenses.filter(searchFilter);

  // KPI Metrics
const totalSpend = totalExpense;

// Funnel Data
const funnelData = [
  { value: totalLeadsCount, name: "Total Leads" },
  { value: accountsOpenedCount, name: `Accounts (${totalLeadsCount > 0 ? (accountsOpenedCount/totalLeadsCount*100).toFixed(0) : 0}%)` },
  { value: kycCompletedCount, name: `KYC (${totalLeadsCount > 0 ? (kycCompletedCount/totalLeadsCount*100).toFixed(0) : 0}%)` },
  { value: activatedCount, name: `Activated (${totalLeadsCount > 0 ? (activatedCount/totalLeadsCount*100).toFixed(0) : 0}%)` }
];

// Campaign Source Comparison
const sourceChartData = campaigns.map((c) => {
  const rows = globalFilteredRevenue.filter(
    (r) => Number(r.campaign_id) === Number(c.id)
  );
  const expRows = globalFilteredExpenses.filter(
    (e) => Number(e.campaign_id) === Number(c.id)
  );

  const rev = rows.reduce(
    (sum, r) => sum + Number(r.amount || 0),
    0
  );
  const actualSpend = expRows.reduce(
    (sum, e) => sum + Number(e.amount || 0),
    0
  );

  return {
    source: c.name,
    spend: actualSpend > 0 ? actualSpend : Number(c.cost || 0),
    revenue: rev
  };
});

// Monthly Trend
const monthlyMap = {};

globalFilteredRevenue.forEach((r) => {
  const month = new Date(r.date).toLocaleString("default", {
    month: "short"
  });

  if (!monthlyMap[month]) {
    monthlyMap[month] = {
      month,
      spend: 0,
      revenue: 0
    };
  }

  monthlyMap[month].revenue += Number(r.amount || 0);
});

globalFilteredExpenses.forEach((e) => {
  const month = new Date(e.date).toLocaleString("default", {
    month: "short"
  });

  if (!monthlyMap[month]) {
    monthlyMap[month] = {
      month,
      spend: 0,
      revenue: 0
    };
  }

  monthlyMap[month].spend += Number(e.amount || 0);
});

const monthlyChartData = Object.values(monthlyMap);


const uploadStatus = {
  agents: agents.length > 0,
  campaigns: campaigns.length > 0,
  leads: leads.length > 0,
  revenue: revenue.length > 0,
  expenses: expenses.length > 0,
};

const topExpenseCategory =
  chartData.length > 0
    ? [...chartData].sort((a, b) => b.amount - a.amount)[0].name
    : "None";

const topCampaign =
  campaignPerformance.length > 0
    ? [...campaignPerformance].sort((a, b) => b.revenue - a.revenue)[0].name
    : "None";

const topAgentName = agentPerformance.length > 0
  ? [...agentPerformance].sort((a, b) => b.revenue - a.revenue)[0].name
  : "None";

const bestROI =
  campaignPerformance.length > 0
    ? [...campaignPerformance].sort((a, b) => b.roi - a.roi)[0].name
    : "None";

const worstROI =
  campaignPerformance.length > 0
    ? [...campaignPerformance].sort((a, b) => a.roi - b.roi)[0].name
    : "None";

const worstCpaCampaign = campaignPerformance.length > 0
  ? [...campaignPerformance].sort((a, b) => Number(b.cpa || 0) - Number(a.cpa || 0))[0].name
  : "None";

const monthlyTrend =
  profitNum > 0 ? "Positive Growth" : "Negative Growth";

  const spendEfficiency = totalExpenseNum > 0 ? (totalRevenueNum / totalExpenseNum).toFixed(2) : totalRevenueNum.toFixed(2);

  let localInsight = "";
  if (totalLeadsCount === 0 && globalFilteredExpenses.length === 0 && globalFilteredRevenue.length === 0) {
    localInsight = "🚀 Welcome! Start by uploading your historic data via the tabs on the left.";
  } else {
    if (cpa > 2000) localInsight += "⚠️ High acquisition cost (CPA > ₹2000). Optimize campaigns.\n";
    if (roi && roi > 200) localInsight += "✅ Strong ROI (> 200%). Consider scaling campaigns.\n";
    if (Number(activationRate) < 30 && totalLeadsCount > 10) localInsight += "⚠️ Low conversion (<30%). Improve funnel.\n";
    if (profitNum < 0) localInsight += "⚠️ Currently operating at a loss. Review your major expense categories.\n";
    if (!localInsight) localInsight = "✅ Metrics look stable. Click 'Generate Insights' for an LLM deep dive.";
  }

  // Wrapped AI function for Dashboard View
  const handleGenerateAI = async () => {
    setLoadingAI(true);
    const res = await generateAIInsight({
      expenses,
      revenue,
      profit,
      roi,
      cpa,
      agentPerformance,
      campaignPerformance,
      activationRate,
      topExpenseCategory,
      topCampaign,
      bestROI,
      worstROI,
      monthlyTrend
    });
    setAiInsight(res);
    setLoadingAI(false);
  };

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) return alert("No data to export.");
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToExcel = (data, filename) => {
    if (!data || data.length === 0) return alert("No data to export.");
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data");
    XLSX.writeFile(wb, `${filename}.xlsx`);
  };

if (loading) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500 text-sm font-semibold tracking-wider uppercase">
      Loading System...
    </div>
  );
}

if (!session) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-200 w-full max-w-md">

        <div className="flex flex-col items-center mb-8">
          <img
            src="/logo.png"
            alt="Anvexa AI Solutions"
            className="w-16 h-16 mb-4"
            onError={(e) => e.target.style.display='none'}
          />
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
            Anvexa AI Solutions Login
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Secure client access portal
          </p>
        </div>

        <input
          type="email"
          placeholder="Email"
          className="w-full border p-3 mb-4 rounded-xl"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <input
          type="password"
          placeholder="Password"
          className="w-full border p-3 mb-3 rounded-xl"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <button
          onClick={handleLogin}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-xl font-semibold"
        >
          Sign In
        </button>

        <button
          onClick={handleForgotPassword}
          className="w-full mt-3 text-sm text-blue-600 hover:underline"
        >
          Forgot Password?
        </button>
      </div>
    </div>
  );
}

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden font-sans text-gray-800">
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} handleLogout={handleLogout} />

      <main className="flex-1 p-8 overflow-y-auto">
        <div className="max-w-6xl mx-auto">

          {/* Header */}
          <header className="flex flex-col md:flex-row md:justify-between md:items-center mb-8 bg-white p-6 rounded-xl shadow-sm border border-slate-200 gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                Anvexa AI Solutions
              </h1>
              <p className="text-slate-500 text-sm mt-1">Intelligent Platform Dashboard {lastSync && `• Last synced: ${lastSync}`}</p>
            </div>
            
            <div className="flex items-center gap-2 bg-slate-100 p-1.5 rounded-lg border border-slate-200">
              <span className="text-xs font-bold text-slate-500 uppercase px-2 tracking-wider">Date:</span>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="bg-white border border-slate-300 rounded-md px-3 py-1.5 text-sm font-semibold focus:ring-2 focus:ring-blue-600 outline-none text-slate-800 shadow-sm cursor-pointer"
              >
                <option value="last_7_days">Last 7 Days</option>
                <option value="last_30_days">Last 30 Days</option>
                <option value="last_90_days">Last 90 Days</option>
                <option value="all_time">All Time</option>
              </select>
            </div>

            <div className="flex-1 max-w-md mx-4 relative">
              <span className="absolute left-3 top-2.5 text-slate-400 text-sm">🔍</span>
              <input 
                type="text" 
                placeholder="Search campaigns, agents, expenses..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-600 outline-none text-sm"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={resetDatabase}
                className="bg-white border border-red-200 text-red-600 hover:bg-red-50 px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-200"
              >
                🧹 Reset Data
              </button>
              <button
                onClick={() => setShowGuide(true)}
                className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-sm transition-all duration-200"
              >
                📘 Help Center
              </button>
            </div>
          </header>

          {/* TAB 1: DASHBOARD */}
          {activeTab === "dashboard" && (
            <DashboardView
              totalRevenue={totalRevenue}
              totalExpense={totalExpense}
              profit={profit}
              cpa={cpa}
              totalSpend={totalSpend}
              activationRate={activationRate}
              totalLeads={totalLeads}
              chartData={chartData}
              funnelData={funnelData}
              sourceChartData={sourceChartData}
              monthlyChartData={monthlyChartData}
              loadingAI={loadingAI}
              aiInsight={aiInsight}
              handleGenerateAI={handleGenerateAI}
              roi={roi}
              localInsight={localInsight}
              uploadStatus={uploadStatus}
              topCampaign={topCampaign}
              topAgentName={topAgentName}
              worstCpaCampaign={worstCpaCampaign}
              exportToCSV={exportToCSV}
              exportToExcel={exportToExcel}
            />
          )}

          {/* TAB 2: CAMPAIGNS */}
          {activeTab === "campaigns" && (
            <Campaigns
              campaigns={campaigns}
              campaignName={campaignName}
              setCampaignName={setCampaignName}
              campaignCost={campaignCost}
              setCampaignCost={setCampaignCost}
              addCampaign={addCampaign}
              searchedCampaignPerformance={searchedCampaignPerformance}
              handleFileUpload={handleFileUpload}
              downloadSampleCSV={downloadSampleCSV}
              inputClass={inputClass}
              exportToCSV={exportToCSV}
              exportToExcel={exportToExcel}
            />
          )}

          {/* TAB 3: AGENTS */}
          {activeTab === "agents" && (
            <Agents
              agents={agents}
              agentName={agentName}
              setAgentName={setAgentName}
              agentSalary={agentSalary}
              setAgentSalary={setAgentSalary}
              addAgent={addAgent}
              searchedAgentPerformance={searchedAgentPerformance}
              handleFileUpload={handleFileUpload}
              downloadSampleCSV={downloadSampleCSV}
              inputClass={inputClass}
              exportToCSV={exportToCSV}
              exportToExcel={exportToExcel}
            />
          )}

          {/* TAB: LEADS */}
          {activeTab === "leads" && (
            <Leads
              agents={agents}
              campaigns={campaigns}
              selectedAgent={selectedAgent}
              setSelectedAgent={setSelectedAgent}
              selectedCampaign={selectedCampaign}
              setSelectedCampaign={setSelectedCampaign}
              leadCreatedDate={leadCreatedDate}
              setLeadCreatedDate={setLeadCreatedDate}
              leadAccountOpenedDate={leadAccountOpenedDate}
              setLeadAccountOpenedDate={setLeadAccountOpenedDate}
              leadKycCompletedDate={leadKycCompletedDate}
              setLeadKycCompletedDate={setLeadKycCompletedDate}
              leadActivatedDate={leadActivatedDate}
              setLeadActivatedDate={setLeadActivatedDate}
              addLead={addLead}
              searchedLeads={searchedLeads}
              handleFileUpload={handleFileUpload}
              downloadSampleCSV={downloadSampleCSV}
              inputClass={inputClass}
              totalLeadsCount={totalLeadsCount}
              accountsOpenedCount={accountsOpenedCount}
              kycCompletedCount={kycCompletedCount}
              activatedCount={activatedCount}
              exportToCSV={exportToCSV}
              exportToExcel={exportToExcel}
            />
          )}

          {/* TAB 4: REVENUE FACTS */}
          {activeTab === "revenue" && (
            <Revenue
              campaigns={campaigns}
              agents={agents}
              canUploadExpenses={canUploadExpenses}
              canUploadRevenue={canUploadRevenue}
              handleFileUpload={handleFileUpload}
              downloadSampleCSV={downloadSampleCSV}
              inputDate={inputDate}
              setInputDate={setInputDate}
              amount={amount}
              setAmount={setAmount}
              selectedCampaign={selectedCampaign}
              setSelectedCampaign={setSelectedCampaign}
              setCategory={setCategory}
              selectedAgent={selectedAgent}
              setSelectedAgent={setSelectedAgent}
              addExpense={addExpense}
              filterDate={filterDate}
              setFilterDate={setFilterDate}
              revenueAmount={revenueAmount}
              setRevenueAmount={setRevenueAmount}
              revenueCategory={revenueCategory}
              setRevenueCategory={setRevenueCategory}
              conversions={conversions}
              setConversions={setConversions}
              addRevenue={addRevenue}
              searchedExpenses={searchedExpenses}
              filteredTotalExpense={filteredTotalExpense}
              inputClass={inputClass}
              profit={profit}
              totalExpense={totalExpense}
              totalRevenue={totalRevenue}
              topExpenseCategory={topExpenseCategory}
              spendEfficiency={spendEfficiency}
              exportToCSV={exportToCSV}
              exportToExcel={exportToExcel}
            />
          )}
        </div>

        <footer className="mt-12 py-8 border-t border-slate-200 text-center text-slate-500 text-sm">
          <p>© {new Date().getFullYear()} Anvexa AI Intelligent Platform</p>
          <p className="mt-1 font-medium">Powered by AI • Built for Growth</p>
        </footer>

            {showGuide && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto">

                  {/* Header */}
                  <div className="bg-slate-900 text-white p-6 rounded-t-xl flex justify-between items-center">
                    <div>
                      <h2 className="text-2xl font-bold">📘 Anvexa AI Help Center</h2>
                      <p className="text-sm opacity-90 mt-1">
                        Everything you need to get value fast
                      </p>
                    </div>

                    <button
                      onClick={() => setShowGuide(false)}
                      className="text-white text-2xl hover:text-slate-300"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="p-8 space-y-8">

                    {/* Quick Start */}
                    <div>
                      <h3 className="text-lg font-bold text-slate-800 mb-3">
                        🚀 Quick Start
                      </h3>

                      <div className="grid md:grid-cols-2 gap-4">

                        <div className="bg-gray-50 p-4 rounded-xl">1️⃣ Add Agents</div>
                        <div className="bg-gray-50 p-4 rounded-xl">2️⃣ Add Campaigns</div>
                        <div className="bg-gray-50 p-4 rounded-xl">3️⃣ Upload Leads</div>
                        <div className="bg-gray-50 p-4 rounded-xl">4️⃣ Upload Expenses</div>
                        <div className="bg-gray-50 p-4 rounded-xl">5️⃣ Upload Revenue</div>
                        <div className="bg-gray-50 p-4 rounded-xl">6️⃣ Review Dashboard</div>
                        <div className="bg-gray-50 p-4 rounded-xl">7️⃣ Generate AI Insights</div>

                      </div>
                    </div>

                    {/* KPI Section */}
                    <div>
                      <h3 className="text-lg font-bold text-slate-800 mb-3">
                        📊 KPI Definitions
                      </h3>

                      <div className="grid md:grid-cols-2 gap-4">

                        <div className="border p-4 rounded-xl">
                          <strong>Profit</strong>
                          <p className="text-sm text-gray-600">Revenue minus Expense</p>
                        </div>

                        <div className="border p-4 rounded-xl">
                          <strong>ROI</strong>
                          <p className="text-sm text-gray-600">
                            (Profit / Expense) × 100
                          </p>
                        </div>

                        <div className="border p-4 rounded-xl">
                          <strong>CPA</strong>
                          <p className="text-sm text-gray-600">
                            Expense per acquisition
                          </p>
                        </div>

                        <div className="border p-4 rounded-xl">
                          <strong>Activation Rate</strong>
                          <p className="text-sm text-gray-600">
                            Converted users %
                          </p>
                        </div>

                      </div>
                    </div>

                    {/* Upload Templates */}
                    <div>
                      <h3 className="text-lg font-bold text-slate-800 mb-3">
                        📁 Upload Format
                      </h3>

                      <div className="bg-gray-100 p-4 rounded-xl text-sm font-mono overflow-x-auto">
            Expenses CSV:
            date,amount,category,agent,campaign

            Revenue CSV:
            date,amount,agent,campaign

            Leads CSV:
            agent_name,campaign_name,created_date,account_opened_date,kyc_completed_date,activated_date
                      </div>
                    </div>

                    {/* Security */}
                    <div>
                      <h3 className="text-lg font-bold text-slate-800 mb-3">
                        🔒 Security
                      </h3>

                      <p className="text-gray-700">
                        Your data is securely linked to your account and isolated from other clients.
                      </p>
                    </div>

                    {/* Support */}
                    <div className="bg-slate-50 border border-slate-200 p-6 rounded-xl">
                      <h3 className="text-lg font-bold text-slate-800 mb-2">
                        📞 Need Help?
                      </h3>

                      <p className="text-gray-700 mb-4">
                        Our team can assist with setup, onboarding and custom analytics.
                      </p>

                      <div className="flex gap-3 flex-wrap">

                        <a
                          href="mailto:support@anvexaai.com"
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                        >
                          Email Support
                        </a>

                        <a
                          href="https://anvexaai.com"
                          target="_blank"
                          rel="noreferrer"
                          className="border border-blue-600 text-blue-600 hover:bg-blue-50 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                        >
                          Visit Website
                        </a>

                      </div>
                    </div>

                  </div>
                </div>
              </div>
            )}
      </main>
    </div>
  );
}
export default App;
